<template>
  <!-- PIN gate -->
  <div v-if="!unlocked" class="flex items-center justify-center min-h-screen bg-gray-50">
    <div class="bg-white rounded-xl shadow-md p-8 w-80 flex flex-col items-center gap-4">
      <div class="text-xl font-bold text-gray-700">Dev Tools</div>
      <div class="text-sm text-gray-400">{{ t('enterPin') }}</div>
      <el-input
        v-model="pin"
        type="password"
        show-password
        :placeholder="t('pinPlaceholder')"
        class="w-full"
        autofocus
        @keydown.enter="checkPin"
      />
      <div v-if="pinError" class="text-sm text-red-500">{{ t('incorrectPin') }}</div>
      <el-button type="primary" class="w-full" @click="checkPin">{{ t('unlock') }}</el-button>
    </div>
  </div>

  <!-- Actual content -->
  <div v-else class="p-6">
    <div class="text-2xl font-bold mb-4">Dev Tools</div>

    <div class="bg-white rounded-lg p-4 shadow-sm">
      <div class="text-lg font-bold mb-3">
        {{ t('locations') }} ({{ locations.length }})
      </div>

      <el-table
        ref="tableRef"
        v-loading="loading"
        :data="locations"
        row-key="id"
        stripe
        size="small"
        @row-click="toggleExpand"
        class="location-table"
      >
        <el-table-column type="expand">
          <template #default="{ row }">
            <div class="px-8 py-3">
              <div class="text-sm font-bold text-gray-500 mb-2">
                {{ t('machines') }} ({{ row.machines?.length || 0 }})
              </div>
              <el-table
                v-if="row.machines?.length"
                :data="row.machines"
                :row-class-name="machineRowClassName"
                size="small"
              >
                <el-table-column :label="t('name')" min-width="160">
                  <template #default="{ row: machine }">
                    {{ $ctx.locale === 'ar' ? machine.arName : machine.enName }}
                  </template>
                </el-table-column>
                <el-table-column label="GUID" min-width="280">
                  <template #default="{ row: machine }">
                    <code class="text-xs bg-gray-100 px-2 py-0.5 rounded select-all">{{ machine.machineGUID }}</code>
                  </template>
                </el-table-column>
                <el-table-column :label="t('virtual')" width="100" align="center">
                  <template #default="{ row: machine }">
                    <el-tag v-if="machine.isVirtual" type="info" size="small">{{ t('yes') }}</el-tag>
                    <span v-else class="text-gray-400">{{ t('no') }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="" width="130" align="center">
                  <template #default="{ row: machine }">
                    <el-tag v-if="machine.machineGUID === currentGUID" type="success" size="small">
                      {{ t('active') }}
                    </el-tag>
                    <el-button
                      v-else
                      size="small"
                      type="primary"
                      @click="switchMachine(machine)"
                    >
                      {{ t('switch') }}
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
              <div v-else class="text-sm text-gray-400">{{ t('noMachines') }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="t('name')" min-width="200">
          <template #default="{ row }">
            {{ $ctx.locale === 'ar' ? row.arName : row.enName }}
          </template>
        </el-table-column>
        <el-table-column :label="t('type')" min-width="120">
          <template #default="{ row }">
            {{ row.type || '—' }}
          </template>
        </el-table-column>
        <el-table-column :label="t('machines')" width="120" align="center">
          <template #default="{ row }">
            <el-tag size="small" type="info">{{ row.machines?.length || 0 }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- API Keys -->
    <div v-if="canViewApiKeys" class="bg-white rounded-lg p-4 shadow-sm mt-6">
      <div class="flex items-center justify-between mb-3">
        <div class="text-lg font-bold">
          {{ t('apiKeys') }} ({{ apiKeys.length }})
        </div>
        <el-button
          v-if="canCreateApiKeys"
          type="primary"
          size="small"
          @click="openCreateKeyDialog"
        >
          <Icon icon="material-symbols:add" class="mr-1" />{{ t('newApiKey') }}
        </el-button>
      </div>

      <el-table
        v-loading="apiKeysLoading"
        :data="apiKeys"
        row-key="id"
        stripe
        size="small"
      >
        <el-table-column :label="t('name')" min-width="160">
          <template #default="{ row }">
            <div class="font-medium">{{ row.name }}</div>
            <div v-if="row.description" class="text-xs text-gray-400">{{ row.description }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Prefix" min-width="170">
          <template #default="{ row }">
            <code class="text-xs bg-gray-100 px-2 py-0.5 rounded select-all">{{ row.prefix }}…</code>
          </template>
        </el-table-column>
        <el-table-column :label="t('user')" min-width="160">
          <template #default="{ row }">
            {{ row.user?.name || '—' }}
          </template>
        </el-table-column>
        <el-table-column :label="t('extraProperties')" min-width="180">
          <template #default="{ row }">
            <div v-if="row.extraProperties && Object.keys(row.extraProperties).length" class="flex flex-wrap gap-1">
              <el-tag
                v-for="(value, key) in row.extraProperties"
                :key="key"
                size="small"
                type="info"
              >
                {{ key }}: {{ value }}
              </el-tag>
            </div>
            <span v-else class="text-gray-400">—</span>
          </template>
        </el-table-column>
        <el-table-column :label="t('lastUsed')" min-width="170">
          <template #default="{ row }">
            <span v-if="row.lastUsedAt">{{ formatPostgresDate(row.lastUsedAt, 'DD/MM/YYYY hh:mm A') }}</span>
            <span v-else class="text-gray-400">{{ t('never') }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="t('status')" width="110" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.isActive" type="success" size="small">{{ t('activeKey') }}</el-tag>
            <el-tag v-else type="info" size="small">{{ t('revoked') }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="" width="170" align="center">
          <template #default="{ row }">
            <el-button
              v-if="canUpdateApiKeys && row.isActive"
              size="small"
              @click="openEditKeyDialog(row)"
            >
              {{ t('edit') }}
            </el-button>
            <el-button
              v-if="canRevokeApiKeys && row.isActive"
              size="small"
              type="danger"
              @click="revokeKey(row)"
            >
              {{ t('revoke') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Create / Edit API key dialog -->
    <el-dialog
      v-model="keyDialogVisible"
      :title="dialogMode === 'create' ? t('newApiKey') : t('editApiKey')"
      width="520px"
    >
      <el-form label-position="top" class="w-full">
        <el-form-item v-if="dialogMode === 'create'" :label="t('user')" required class="w-full">
          <el-select
            v-model="keyForm.userID"
            filterable
            clearable
            class="w-full"
            :placeholder="t('selectUser')"
          >
            <el-option
              v-for="u in users"
              :key="u.id"
              :label="u.name"
              :value="u.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="t('name')" required class="w-full">
          <el-input v-model="keyForm.name" class="w-full" :placeholder="t('apiKeyNamePlaceholder')" maxlength="127" />
        </el-form-item>
        <el-form-item :label="t('description')" class="w-full">
          <el-input v-model="keyForm.description" class="w-full" type="textarea" :rows="2" maxlength="255" />
        </el-form-item>
        <el-form-item :label="t('extraProperties')" class="w-full">
          <div class="w-full">
            <div
              v-for="(prop, index) in keyForm.extraProps"
              :key="index"
              class="flex items-center gap-2 mb-2"
            >
              <el-input v-model="prop.key" :placeholder="t('propertyKey')" class="flex-1" maxlength="64" />
              <el-input v-model="prop.value" :placeholder="t('propertyValue')" class="flex-1" maxlength="255" />
              <el-button text type="danger" @click="removePropRow(index)">
                <Icon icon="material-symbols:delete-outline" width="18" height="18" />
              </el-button>
            </div>
            <div v-if="!keyForm.extraProps.length" class="text-xs text-gray-400 mb-2">{{ t('noProperties') }}</div>
            <el-button size="small" @click="addPropRow">
              <Icon icon="material-symbols:add" class="mr-1" />{{ t('addProperty') }}
            </el-button>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="keyDialogVisible = false">{{ t('cancel') }}</el-button>
        <el-button
          type="primary"
          :loading="saving"
          :disabled="!keyForm.name || (dialogMode === 'create' && !keyForm.userID)"
          @click="saveKey"
        >
          {{ dialogMode === 'create' ? t('create') : t('save') }}
        </el-button>
      </template>
    </el-dialog>

    <!-- Created key reveal dialog -->
    <el-dialog
      v-model="revealDialogVisible"
      :title="t('apiKeyCreated')"
      width="520px"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        class="mb-4"
        :title="t('keyWarningTitle')"
        :description="t('keyWarningBody')"
      />
      <div class="flex items-center gap-2 bg-gray-100 rounded p-3">
        <code class="text-sm break-all flex-1 select-all">{{ createdKey }}</code>
        <el-button size="small" type="primary" @click="copyKey">
          <Icon :icon="copied ? 'material-symbols:check' : 'material-symbols:content-copy-outline'" class="mr-1" />{{ copied ? t('copied') : t('copy') }}
        </el-button>
      </div>
      <template #footer>
        <el-button type="primary" @click="dismissReveal">{{ t('done') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHTTP } from '@/plugins/http'
import { useAuth } from '@/plugins/auth'
import { can } from '@/plugins/access-control'
import Location from '@/models/company/Location'
import User from '@/models/user/User'
import { formatPostgresDate } from '@/utils/date'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Icon } from '@iconify/vue'
import cheatbeep from '@/assets/sounds/cheat.mp3'

const API_KEYS_ENDPOINT = '/user/api-keys'

const DEV_TOOLS_PIN = 'salom'

export default defineComponent({
  name: 'DevTools',
  components: { Icon },
  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          enterPin: 'Enter PIN to continue',
          pinPlaceholder: 'PIN',
          incorrectPin: 'Incorrect PIN',
          unlock: 'Unlock',
          locations: 'Locations',
          name: 'Name',
          type: 'Type',
          machines: 'Machines',
          noMachines: 'No machines in this location',
          virtual: 'Virtual',
          yes: 'Yes',
          no: 'No',
          active: 'Active',
          switch: 'Switch',
          switchingTo: 'Switching to',
          apiKeys: 'API Keys',
          newApiKey: 'New API Key',
          user: 'User',
          selectUser: 'Select a user',
          description: 'Description',
          apiKeyNamePlaceholder: 'e.g. doctorx integration',
          lastUsed: 'Last Used',
          never: 'Never',
          status: 'Status',
          activeKey: 'Active',
          revoked: 'Revoked',
          revoke: 'Revoke',
          create: 'Create',
          save: 'Save',
          edit: 'Edit',
          editApiKey: 'Edit API Key',
          extraProperties: 'Extra Properties',
          addProperty: 'Add property',
          noProperties: 'No properties',
          propertyKey: 'Key',
          propertyValue: 'Value',
          cancel: 'Cancel',
          done: 'Done',
          copy: 'Copy',
          copied: 'Copied',
          apiKeyCreated: 'API Key Created',
          keyWarningTitle: "Copy this key now — you won't be able to see it again",
          keyWarningBody: 'This is the only time the full key is shown. After you dismiss this dialog, only the prefix is recoverable.',
          confirmRevokeTitle: 'Revoke API Key',
          confirmRevokeBody: 'This will immediately disable the key “{name}”. This cannot be undone.',
          keyCreatedMsg: 'API key created',
          keyUpdatedMsg: 'API key updated',
          keyRevokedMsg: 'API key revoked',
          fetchKeysFailed: 'Failed to fetch API keys',
          createKeyFailed: 'Failed to create API key',
          updateKeyFailed: 'Failed to update API key',
          revokeKeyFailed: 'Failed to revoke API key'
        },
        ar: {
          enterPin: 'أدخل الرمز للمتابعة',
          pinPlaceholder: 'الرمز',
          incorrectPin: 'رمز غير صحيح',
          unlock: 'فتح',
          locations: 'المواقع',
          name: 'الاسم',
          type: 'النوع',
          machines: 'الاجهزة',
          noMachines: 'لا توجد اجهزة في هذا الموقع',
          virtual: 'افتراضي',
          yes: 'نعم',
          no: 'لا',
          active: 'الحالي',
          switch: 'تبديل',
          switchingTo: 'التبديل الى',
          apiKeys: 'مفاتيح API',
          newApiKey: 'مفتاح API جديد',
          user: 'المستخدم',
          selectUser: 'اختر مستخدم',
          description: 'الوصف',
          apiKeyNamePlaceholder: 'مثال: تكامل doctorx',
          lastUsed: 'آخر استخدام',
          never: 'لم يُستخدم',
          status: 'الحالة',
          activeKey: 'فعّال',
          revoked: 'ملغى',
          revoke: 'إلغاء',
          create: 'إنشاء',
          save: 'حفظ',
          edit: 'تعديل',
          editApiKey: 'تعديل مفتاح API',
          extraProperties: 'خصائص إضافية',
          addProperty: 'إضافة خاصية',
          noProperties: 'لا توجد خصائص',
          propertyKey: 'المفتاح',
          propertyValue: 'القيمة',
          cancel: 'إلغاء',
          done: 'تم',
          copy: 'نسخ',
          copied: 'تم النسخ',
          apiKeyCreated: 'تم إنشاء مفتاح API',
          keyWarningTitle: 'انسخ هذا المفتاح الآن — لن تتمكن من رؤيته مرة أخرى',
          keyWarningBody: 'هذه هي المرة الوحيدة التي يُعرض فيها المفتاح كاملاً. بعد إغلاق هذه النافذة، يمكن استرجاع البادئة فقط.',
          confirmRevokeTitle: 'إلغاء مفتاح API',
          confirmRevokeBody: 'سيؤدي هذا إلى تعطيل المفتاح «{name}» فوراً. لا يمكن التراجع عن ذلك.',
          keyCreatedMsg: 'تم إنشاء مفتاح API',
          keyUpdatedMsg: 'تم تحديث مفتاح API',
          keyRevokedMsg: 'تم إلغاء مفتاح API',
          fetchKeysFailed: 'فشل في جلب مفاتيح API',
          createKeyFailed: 'فشل في إنشاء مفتاح API',
          updateKeyFailed: 'فشل في تحديث مفتاح API',
          revokeKeyFailed: 'فشل في إلغاء مفتاح API'
        }
      }
    })

    const http = useHTTP()
    const { resetAuthState } = useAuth()
    const tableRef = ref()
    const locations = ref<any[]>([])
    const loading = ref(true)
    const currentGUID = ref(localStorage.getItem('machineGUID') || '')

    const unlocked = ref(false)
    const pin = ref('')
    const pinError = ref(false)

    // API keys
    const canViewApiKeys = computed(() => can('GET', API_KEYS_ENDPOINT))
    const canCreateApiKeys = computed(() => can('POST', API_KEYS_ENDPOINT))
    const canUpdateApiKeys = computed(() => can('PUT', API_KEYS_ENDPOINT))
    const canRevokeApiKeys = computed(() => can('DELETE', API_KEYS_ENDPOINT))

    const apiKeys = ref<any[]>([])
    const apiKeysLoading = ref(false)
    const users = ref<User[]>([])
    const keyDialogVisible = ref(false)
    const dialogMode = ref<'create' | 'edit'>('create')
    const editingId = ref('')
    const saving = ref(false)
    const keyForm = ref({
      name: '',
      description: '',
      userID: '',
      extraProps: [] as Array<{ key: string; value: string }>
    })
    const revealDialogVisible = ref(false)
    const createdKey = ref('')
    const copied = ref(false)

    function checkPin() {
      if (pin.value === DEV_TOOLS_PIN) {
        unlocked.value = true
        pinError.value = false
        fetchLocations()
        loadApiKeysSection()
      } else {
        pinError.value = true
        pin.value = ''
      }
    }

    function loadApiKeysSection() {
      if (!canViewApiKeys.value) return
      fetchApiKeys()
      fetchUsers()
    }

    async function fetchApiKeys() {
      apiKeysLoading.value = true
      try {
        const data = await http.get<any[]>(API_KEYS_ENDPOINT)
        apiKeys.value = data || []
      } catch (e: any) {
        ElMessage.error(e?.title || t('fetchKeysFailed'))
      } finally {
        apiKeysLoading.value = false
      }
    }

    async function fetchUsers() {
      try {
        const data = await http.get<User[]>(User.ENDPOINT)
        users.value = data || []
      } catch {
        users.value = []
      }
    }

    function propsObjectToRows(obj: Record<string, any> | null | undefined) {
      if (!obj) return []
      return Object.keys(obj).map((key) => ({ key, value: obj[key] == null ? '' : String(obj[key]) }))
    }

    function rowsToPropsObject(rows: Array<{ key: string; value: string }>) {
      const obj: Record<string, string> = {}
      rows.forEach((row) => {
        const key = row.key.trim()
        if (key) obj[key] = row.value
      })
      return obj
    }

    function openCreateKeyDialog() {
      dialogMode.value = 'create'
      editingId.value = ''
      keyForm.value = { name: '', description: '', userID: '', extraProps: [] }
      keyDialogVisible.value = true
    }

    function openEditKeyDialog(row: any) {
      dialogMode.value = 'edit'
      editingId.value = row.id
      keyForm.value = {
        name: row.name || '',
        description: row.description || '',
        userID: row.userID || '',
        extraProps: propsObjectToRows(row.extraProperties)
      }
      keyDialogVisible.value = true
    }

    function addPropRow() {
      keyForm.value.extraProps.push({ key: '', value: '' })
    }

    function removePropRow(index: number) {
      keyForm.value.extraProps.splice(index, 1)
    }

    async function saveKey() {
      if (!keyForm.value.name) return
      if (dialogMode.value === 'create' && !keyForm.value.userID) return
      saving.value = true
      try {
        const extraProperties = rowsToPropsObject(keyForm.value.extraProps)
        if (dialogMode.value === 'create') {
          const payload: any = {
            name: keyForm.value.name,
            userID: keyForm.value.userID,
            extraProperties
          }
          if (keyForm.value.description) payload.description = keyForm.value.description
          const res = await http.post<any>(API_KEYS_ENDPOINT, payload)
          keyDialogVisible.value = false
          createdKey.value = res?.key || ''
          copied.value = false
          revealDialogVisible.value = true
          ElMessage.success(t('keyCreatedMsg'))
          fetchApiKeys()
        } else {
          const payload: any = {
            name: keyForm.value.name,
            description: keyForm.value.description || '',
            extraProperties
          }
          await http.put(`${API_KEYS_ENDPOINT}/${editingId.value}`, payload)
          keyDialogVisible.value = false
          ElMessage.success(t('keyUpdatedMsg'))
          fetchApiKeys()
        }
      } catch (e: any) {
        ElMessage.error(
          e?.title || (dialogMode.value === 'create' ? t('createKeyFailed') : t('updateKeyFailed'))
        )
      } finally {
        saving.value = false
      }
    }

    async function revokeKey(row: any) {
      try {
        await ElMessageBox.confirm(
          t('confirmRevokeBody', { name: row.name }),
          t('confirmRevokeTitle'),
          { type: 'warning', confirmButtonText: t('revoke'), cancelButtonText: t('cancel') }
        )
      } catch {
        return
      }
      try {
        await http.delete(`${API_KEYS_ENDPOINT}/${row.id}`)
        ElMessage.success(t('keyRevokedMsg'))
        fetchApiKeys()
      } catch (e: any) {
        ElMessage.error(e?.title || t('revokeKeyFailed'))
      }
    }

    async function copyKey() {
      try {
        await navigator.clipboard.writeText(createdKey.value)
        copied.value = true
      } catch {
        copied.value = false
      }
    }

    function dismissReveal() {
      revealDialogVisible.value = false
      createdKey.value = ''
      copied.value = false
    }

    function toggleExpand(row: any) {
      tableRef.value?.toggleRowExpansion(row)
    }

    function machineRowClassName({ row }: { row: any }) {
      return row.machineGUID === currentGUID.value ? 'active-machine-row' : ''
    }

    async function fetchLocations() {
      loading.value = true
      try {
        const data = await http.get<any[]>(Location.ENDPOINT)
        locations.value = data || []
      } catch (e: any) {
        ElMessage.error(e?.title || 'Failed to fetch locations')
      } finally {
        loading.value = false
      }
    }

    function switchMachine(machine: any) {
      // A virtual machine has no real machine id — clear it so the backend resolves
      // the location's virtual machine (an empty machine-id).
      if (machine.isVirtual) {
        localStorage.removeItem('machineGUID')
      } else {
        localStorage.setItem('machineGUID', machine.machineGUID)
      }
      const audio = new Audio(cheatbeep)
      audio.play()
      ElMessage.success(`${t('switchingTo')} ${machine.enName || machine.name}...`)
      setTimeout(() => {
        resetAuthState()
        window.location.href = '/login'
      }, 500)
    }

    onMounted(() => {
      if (unlocked.value) {
        fetchLocations()
        loadApiKeysSection()
      }
    })

    return {
      t,
      formatPostgresDate,
      tableRef,
      locations,
      loading,
      currentGUID,
      unlocked,
      pin,
      pinError,
      checkPin,
      toggleExpand,
      machineRowClassName,
      switchMachine,
      // API keys
      canViewApiKeys,
      canCreateApiKeys,
      canUpdateApiKeys,
      canRevokeApiKeys,
      apiKeys,
      apiKeysLoading,
      users,
      keyDialogVisible,
      dialogMode,
      saving,
      keyForm,
      revealDialogVisible,
      createdKey,
      copied,
      openCreateKeyDialog,
      openEditKeyDialog,
      addPropRow,
      removePropRow,
      saveKey,
      revokeKey,
      copyKey,
      dismissReveal
    }
  }
})
</script>

<style scoped>
:deep(.active-machine-row) {
  background-color: rgba(var(--el-color-success-rgb), 0.08) !important;
}

:deep(.location-table .el-table__row) {
  cursor: pointer;
}
</style>
